import {
  Box,
  RadioSelectableCard,
  RadioSelectableCardProps,
  Spacer,
  Spinner,
  Typography,
} from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import OccupationSelectorSearchModal from '~/components/OccupationSelectorSearchModal';
import SearchOccupationsListItemsField from '~/components/SearchOccupationsListItemsField';
import LinkButton from '~/components-ds/LinkButton';
import Loading from '~/components-ds/Loading';
import { OCCUPATION_LIMIT } from '~/constants/domain-specific';
import { useAppContent, useAppDispatch, useBusinessDetails } from '~/hooks';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { useGetOccupationsQuery } from '~/services/refData';
import { updateOccupation } from '~/slices/businessDetails';
import { updateDynamicCoverQuestions } from '~/slices/coverDetails';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { selectOccupationAction } from '~/thunks/selectOccupationAction';
import { occupationSearchTypes } from '~/types/analytics';
import { OccupationsListItem } from '~/types/refData';
import { utcDateFormatter } from '~/utils';
import { getActivePagePath } from '~/utils/analytics/helpers';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { isDefined } from '~/utils/isDefined';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

export interface OccupationSelectorProps {
  onSelect?: () => void;
}

const OccupationSelector: React.FC<OccupationSelectorProps> = ({ onSelect }) => {
  const { getTextContent: getSharedContent } = useAppContent({
    basePath: 'shared',
  });

  const { getTextContent } = useAppContent({
    basePath: 'dsme.businessDetailsOccupancySearch',
  });

  const dispatch = useAppDispatch();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { industry, occupationCategory, occupationItem, coverStart, occupation } =
    useBusinessDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const { occupationsList } = useBusinessDetails();
  // Removed local selectedId state; use occupation?.anzsicCode from global state
  // No need to reset selectedId, handled by global state

  const { data, error, isLoading, isFetching } = useGetOccupationsQuery({
    industryCode: industry!.industryCode,
    occupationCategory: occupationCategory!.categoryCode,
    effectiveDate: utcDateFormatter(coverStart!),
  });

  useRTKQErrorHandler(error);

  useEffect(() => {
    // Wait for all necessary API responses to complete before firing the event.
    // If either the data is still loading (`isLoading`) or being fetched (`isFetching`),
    // do nothing and wait for the state to resolve.
    if (isLoading || isFetching) return;

    // Once the API responses are complete and the DOM has been updated with the new data,
    // dispatch the "optimize.activate" event using QAL (QBE Analytics Library).
    // This event notifies Webtrends (or similar analytics tools) that the page is ready
    // with updated dynamic content, ensuring accurate visibility tracking.

    dispatch(
      qalTrackEventAction({
        event: 'optimize.activate',
        data: {
          virtualPagePath: getActivePagePath(),
        },
      })
    );
  }, [isLoading, isFetching, dispatch]);

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  const handleSelect = useCallback(
    async (id: string) => {
      handleFormStart();
      if (occupation?.anzsicCode) {
        dispatch(updateOccupation(undefined));
        dispatch(updateDynamicCoverQuestions([]));
      } else {
        await dispatch(
          selectOccupationAction({
            anzsic: id,
            searchType: occupationSearchTypes.iconSearch,
          })
        ).unwrap();
        onSelect?.();
      }
    },
    [dispatch, handleFormStart, onSelect, occupation?.anzsicCode]
  );

  const scopedOccupationsListItems: Array<OccupationsListItem> = useMemo(() => {
    if (!occupationsList) return [];
    return occupationsList.filter(
      (occ) =>
        occ?.industryCode === industry!.industryCode &&
        occ?.categoryCode === occupationCategory!.categoryCode &&
        (!isPropertyOwner || occ.propertyOwnerAnzsic)
    );
  }, [industry, isPropertyOwner, occupationCategory, occupationsList]);

  const radioListOptions = useMemo(() => {
    const selectedIdFromState = occupation?.anzsicCode; // Use a more descriptive name without leading underscore
    const occupationMap = new Map(scopedOccupationsListItems.map((occ) => [occ?.code, occ]));
    let options =
      data?.occupations
        .slice()
        .filter((occ) => !isPropertyOwner || occ.propertyOwnerAnzsic)
        .sort((a, b) => a.anzsicPriority - b.anzsicPriority)
        .map((occ) => occupationMap.get(occ.anzsicCode))
        .filter(isDefined) ?? [];

    if (selectedIdFromState) {
      options = [options?.find((o) => o.code === selectedIdFromState)].filter(isDefined);
    }
    options = options?.slice(0, 6);

    return (options ?? []).map((option) => ({
      checked: option.code === selectedIdFromState,
      id: option.code,
      value: option.code,
      label: option.desc,
      subText: option.anzsicShortDescription,
      'data-track': isPropertyOwner
        ? "Select your tenant's exact occupation"
        : 'Select your exact occupation',
      'data-track-value': 'capture',
    })) as RadioSelectableCardProps['radioList'];
  }, [data?.occupations, isPropertyOwner, scopedOccupationsListItems, occupation?.anzsicCode]);

  if (isLoading || isFetching) {
    return (
      <Box textAlign="center">
        <Loading />
      </Box>
    );
  }

  return (
    <>
      <Spinner loading={isLoading || isFetching} description={getSharedContent('loadingLabel')} />
      <RadioSelectableCard
        fullWidth
        title={`${getTextContent('fields.iconSearchExactOccupation.titlePrefix')}${
          isPropertyOwner
            ? getTextContent('fields.iconSearchExactOccupation.landlordTitle')
            : getTextContent('fields.iconSearchExactOccupation.businessOperatorTitle')
        }`}
        direction="vertical"
        labelProps={{
          variant: 'body1',
          weight: 'bold',
        }}
        name="occupation-option"
        defaultValue={occupationItem?.code ?? ''}
        onChange={handleSelect}
        radioList={radioListOptions}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        sx={{
          minWidth: 'unset',
        }}
        titleSuffix={
          <ContextualHelpWithButton
            tracking={`help: ${
              isPropertyOwner
                ? "Select your tenant's exact occupation"
                : 'Select your exact occupation'
            }`}
          >
            <Typography>
              {getTextContent('contextualhelpers.iconSearchExactOccupation.description')}
            </Typography>
          </ContextualHelpWithButton>
        }
      />

      <Box id="all-specific-occupations">
        {(data?.occupations?.length ?? 0) > OCCUPATION_LIMIT && !occupation?.anzsicCode && (
          <Box textAlign="center">
            <Spacer size="md" />
            <LinkButton
              label={getTextContent('buttons.seeAllExactOccupations')}
              onClick={() => {
                setIsSearchOpen(true);
              }}
              dataTrack="see all occupations list"
              sx={{ textDecoration: 'underline' }}
            />
          </Box>
        )}

        {occupation?.anzsicCode && (
          <Box textAlign="center">
            <Spacer size="md" />
            <LinkButton
              label={getTextContent('buttons.backToExactOccupations')}
              onClick={() => {
                dispatch(updateOccupation(undefined));
                dispatch(updateDynamicCoverQuestions([]));
              }}
              dataTrack="back to occupations list"
              sx={{ textDecoration: 'underline' }}
            />
          </Box>
        )}
      </Box>

      {isSearchOpen && (
        <OccupationSelectorSearchModal closeModal={() => setIsSearchOpen(false)}>
          <SearchOccupationsListItemsField
            list={scopedOccupationsListItems}
            selectItem={(item) =>
              handleSelect(item.code).then(() => {
                setIsSearchOpen(false);
              })
            }
            showAllBeforeFiltered
          />
        </OccupationSelectorSearchModal>
      )}
    </>
  );
};

export default OccupationSelector;
